// native
import { Component, OnInit, ViewChild, ElementRef, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, Input } from '@angular/core';
import { map, startWith, Subject, Subscription, switchMap, timer } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';

// service
import { ItemsDisplayService } from '../../../core/services/items-display.service';
import { CollectionsService } from '../../../core/services/collections.service';
import { ListService } from '../../../core/services/list.service';
import { TagsService } from '../../../core/services/tags.service';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { MatcherService } from '../../../core/services/matcher.service';

// models
import { ICollection, IDisplaySearchPick, IList, ISearchPick } from '../../../models';

// pipe
import { BoldSpanPipe } from '../../pipes/bold-span.pipe';

// constants
import { DEFAULT_LIBRARY_SORT_COLUMN, DEFAULT_SEARCH_SORT_COLUMN } from '../../../constants/constants';

const MAX_SHOWED_HISTORY_ITEMS = 5;
const MAX_SHOWED_TAG_ITEMS = 5;

const MAX_STORED_HISTORY_ITEMS = 500;

@Component({
  selector: 'scs-search',
  templateUrl: './search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onOutsideDropdownClick($event)'
  }
})
export class SearchComponent implements OnInit, OnDestroy {
  @ViewChild('sortBox') sortBoxElement: ElementRef;
  @ViewChild('searchInput') searchInputElement: ElementRef;
  @ViewChild('searchPicksBox') searchPicksBoxElement: ElementRef;
  @ViewChild('searchKeyBox') searchKeyBoxElement: ElementRef;

  @Input() matcherMode: boolean = false;

  isGlobalSearch: boolean = false;

  globalSearchKeys = {
    'author:"(CARET)"': 'Author',
    'title:"(CARET)"': 'Title',
    'journal:"(CARET)"': 'Journal',
    'year:(CARET)': 'Year'
  };

  librarySearchKeys = {
    ' author:"(CARET)"': 'Author',
    ' title:"(CARET)"': 'Title',
    ' journal:"(CARET)"': 'Journal',
    ' year:(CARET)': 'Year',
    ' notes:"(CARET)"': 'Notes',
    ' citekey:"(CARET)"': 'Citekey',
    ' tag:"(CARET)"': 'Tag'
  };

  searchKeys;

  librarySortOptions = {
    'created': 'Date Added',
    'first_author': 'First Author',
    'last_author': 'Last Author',
    'journal': 'Journal',
    'last_read': 'Recently Read',
    'title': 'Title',
    'year': 'Year'
  };

  globalSortOptions = {
    'relevance': 'Relevance',
    'citations': 'Citation Count',
    'altmetric': 'Altmetric Score',
    'year': 'Year of Publication'
  };

  sortOptions;

  orderOptions = {
    'asc': 'Ascending',
    'desc': 'Descending'
  };

  isSortDropdownVisible: boolean = false;

  term: string;

  currentLibrary: ICollection;
  currentList: IList;
  currentLibrarySubscription: Subscription;
  currentListSubscription: Subscription;

  typeaheadSearch$: Subject<void> = new Subject<void>();
  tagSearchPicks: ISearchPick[] = [];
  displayedSearchPicks: IDisplaySearchPick[] = [];
  isSearchPickDropdownVisible: boolean = false;

  isSearchKeyDropdownVisible = false;

  boldSpanPipe: BoldSpanPipe;

  matcherSubscription: Subscription;

  constructor(
    public collectionsService: CollectionsService,
    public listService: ListService,
    public itemsDisplayService: ItemsDisplayService,
    private tagsService: TagsService,
    private localStorageService: LocalStorageService,
    private changeDetectorRef: ChangeDetectorRef,
    public sanitizer: DomSanitizer,
    private matcherService: MatcherService
  ) {
    this.boldSpanPipe = new BoldSpanPipe(this.sanitizer);
  }

  ngOnInit(): void {
    this.currentLibrarySubscription = this.collectionsService.currentLibrary$.pipe(
      startWith(this.collectionsService.personalLibrary)
    ).subscribe((collection: ICollection) => {
      this.currentLibrary = collection;
      this.changeDetectorRef.markForCheck();
      this.getTagSearchPicks();
    });

    this.currentListSubscription = this.listService.currentList$.subscribe((list: IList) => {
      this.currentList = list;
      this.changeDetectorRef.markForCheck();
    });

    this.sortOptions = { ...this.librarySortOptions };
    this.searchKeys = { ...this.librarySearchKeys };
    this.itemsDisplayService.sort = DEFAULT_LIBRARY_SORT_COLUMN;

    this.typeaheadSearch$.pipe(
      switchMap(() => timer(500))
    ).subscribe(() => {
      this.getSearchPicks();
    });

    if (this.matcherMode) {
      this.matcherSubscription = this.matcherService.currentItem$.subscribe(item => {
        if (!item)
          return;
        this.term = item.article?.title || '';
        setTimeout(() => {
          this.search();
        }, 100);
      });
    }
  }

  ngOnDestroy() {
    this.currentLibrarySubscription?.unsubscribe();
    this.currentListSubscription?.unsubscribe();
    this.matcherSubscription?.unsubscribe();
  }

  getItemSearchPlaceholder() {
    let text = 'Search within ';

    if (this.matcherMode)
      return text + this.collectionsService.collections.find(c => c.id === this.matcherService.matcherCollectionId)?.name || 'library';

    if (this.currentList)
      return text + this.currentList.name;
    return text + (this.currentLibrary?.name || 'My Library');
  }

  onInput() {
    this.typeaheadSearch$.next();
    this.isSearchPickDropdownVisible = true;
  }

  toggleSearch() {
    if (this.matcherMode)
      return;

    this.isGlobalSearch = !this.isGlobalSearch;
    this.sortOptions = this.isGlobalSearch ? { ...this.globalSortOptions } : { ...this.librarySortOptions };
    this.searchKeys = this.isGlobalSearch ? { ...this.globalSearchKeys } : { ...this.librarySearchKeys };

    if (!Object.keys(this.sortOptions).includes(this.itemsDisplayService.sort))
      this.itemsDisplayService.sort = this.isGlobalSearch ? DEFAULT_SEARCH_SORT_COLUMN : DEFAULT_LIBRARY_SORT_COLUMN;

    this.search();
  }

  onClearSearch() {
    this.term = null;
    this.isGlobalSearch ? this.onBackClick() : this.search();
  }

  onBackClick() {
    this.itemsDisplayService.itemsVisible$.next(false);
    this.listService.currentList$.next(null);

    this.isSortDropdownVisible = false;
    this.isSearchPickDropdownVisible = false;
    this.isSearchPickDropdownVisible = false;
  }

  onOutsideDropdownClick(event) {
    if (!this.sortBoxElement?.nativeElement.contains(event.target)) {
      this.isSortDropdownVisible = false;
    }

    if (!this.searchInputElement?.nativeElement.contains(event.target)) {
      this.isSearchPickDropdownVisible = false;
    }

    if (!this.searchKeyBoxElement?.nativeElement.contains(event.target)) {
      this.isSearchKeyDropdownVisible = false;
    }
  }

  onSortChange(key: string) {
    this.isSortDropdownVisible = false;
    this.itemsDisplayService.sort = key;
    this.search();
  }

  onOrderChange(key: 'asc' | 'desc') {
    this.isSortDropdownVisible = false;
    this.itemsDisplayService.order = key;
    this.search();
  }

  private getSearchPicks() {
    const historyPicks = this.getSearchHistory(this.currentLibrary?.id)
      .map<ISearchPick>(value => {
        return { type: 'history', value };
      });

    const allSearchPicks = this.isGlobalSearch ? [...historyPicks] : this.tagSearchPicks.concat(historyPicks);
    this.displayedSearchPicks = this.getDisplayedSearchPicks(allSearchPicks);
    this.changeDetectorRef.markForCheck();
  }

  private getTagSearchPicks() {
    this.tagsService.getTags(this.currentLibrary?.id).pipe(
      map(tags => {
        return tags.map<ISearchPick>(tag => {
          return { type: 'tag', value: tag.id };
        });
      }),
    ).subscribe(picks => {
      this.tagSearchPicks = picks;
    });
  }

  private getSearchHistory(key: string): string[] {
    const storageKey = ['search_history', key].join('_');
    return this.localStorageService.getByKey(storageKey) || [];
  }

  private pushSearchHistory(key: string, term: string): string[] {
    const storageKey = ['search_history', key].join('_');
    let storedHistory = this.getSearchHistory(key);
    if (!term)
      return storedHistory;

    storedHistory = storedHistory.filter(v => v !== term);
    storedHistory.splice(0, 0, term);

    if (storedHistory.length > MAX_STORED_HISTORY_ITEMS)
      storedHistory = storedHistory.slice(0, MAX_STORED_HISTORY_ITEMS);

    this.localStorageService.save(storageKey, storedHistory);
    return storedHistory;
  }

  private getDisplayedSearchPicks(searchPicks: ISearchPick[]): IDisplaySearchPick[] {
    if (!this.term) {
      return searchPicks
        .filter(v => v.type === 'history')
        .map(v => {
          return {
            type: v.type,
            value: v.value,
            richTextValue: v.value
          };
        })
        .slice(0, MAX_SHOWED_HISTORY_ITEMS);
    }

    const historyPicks = searchPicks
      .filter(v => v.type === 'history')
      .filter(v => v.value.toLowerCase().includes(this.term.toLowerCase()))
      .map(v => {
        return {
          type: v.type,
          value: v.value,
          richTextValue: this.boldSpanPipe.transform(v.value, this.term)
        };
      })
      .slice(0, MAX_SHOWED_HISTORY_ITEMS);

    const tagPicks = searchPicks
      .filter(v => v.type === 'tag')
      .filter(v => v.value.toLowerCase().includes(this.term.toLowerCase()))
      .map(v => {
        return {
          type: v.type,
          value: v.value,
          richTextValue: this.boldSpanPipe.transform(v.value, this.term)
        };
      })
      .slice(0, MAX_SHOWED_TAG_ITEMS);

    return historyPicks.concat(tagPicks);
  }

  search() {
    if (this.matcherMode)
      return this.searchFromMatcher();

    !this.isGlobalSearch && this.itemsDisplayService.itemParams$.next({
      collection_id: this.currentLibrary?.id,
      list_id: this.currentList?.id,
      query: this.term
    });

    this.isGlobalSearch && this.itemsDisplayService.itemParams$.next({
      isGlobalSearch: true,
      query: this.term
    });

    this.pushSearchHistory(this.currentLibrary?.id, this.term);
    this.isSearchPickDropdownVisible = false;
  }

  searchFromMatcher() {
    this.itemsDisplayService.itemParams$.next({
      collection_id: this.matcherService.matcherCollectionId,
      query: this.term
    });

    this.isSearchPickDropdownVisible = false;
  }

  onSearchPickClick(pick: ISearchPick) {
    this.isSearchPickDropdownVisible = false;
    this.term = pick.type === 'tag' ? `tag:"${pick.value}"` : pick.value;
    this.search();
  }

  onSearchKeyClick(event: MouseEvent, key: string) {
    this.isSearchKeyDropdownVisible = false;
    let q = (this.term || '').concat(key).trim();
    let i = q.indexOf('(CARET)');
    q = q.replace('(CARET)', '');
    setTimeout(() => {
      this.setCaretPosition(this.searchInputElement.nativeElement, i);
    }, 100);
    this.term = q;
  }

  private setCaretPosition(elem: any, pos: number) {
    if (elem.setSelectionRange) {
      elem.focus();
      elem.setSelectionRange(pos, pos);
    } else if (elem.createTextRange) {
      var range = elem.createTextRange();
      range.collapse(true);
      range.moveEnd('character', pos);
      range.moveStart('character', pos);
      range.select();
    }
  }
}
